import { useState, useEffect, useCallback } from 'react'
import { fetchFeedbackList } from '../api/feedbackApi'
import { appToast } from '../../../../components/AppToast'

function formatDate(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function calculatePeriodDates(periodVal) {
  const today = new Date()
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  switch (String(periodVal)) {
    case '1':
    case 'Current Year': {
      const from = new Date(now.getFullYear(), 0, 1)
      const till = new Date(now.getFullYear(), 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '2':
    case 'Current Month': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1)
      const till = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '3':
    case 'Current Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + (day === 0 ? -6 : 1) // Monday
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '4':
    case 'Last Year': {
      const from = new Date(now.getFullYear() - 1, 0, 1)
      const till = new Date(now.getFullYear() - 1, 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '5':
    case 'Last Month': {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const till = new Date(now.getFullYear(), now.getMonth(), 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '6':
    case 'Last Week': {
      const day = now.getDay()
      const diff = now.getDate() - day - 6 + (day === 0 ? -6 : 1)
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '7':
    case 'Next Year': {
      const from = new Date(now.getFullYear() + 1, 0, 1)
      const till = new Date(now.getFullYear() + 1, 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '8':
    case 'Next Month': {
      const from = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      const till = new Date(now.getFullYear(), now.getMonth() + 2, 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '9':
    case 'Next Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + 8 + (day === 0 ? -6 : 1)
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '12':
    case 'Today': {
      return { fromDate: formatDate(now), tillDate: formatDate(now) }
    }
    case '13':
    case 'Yesterday': {
      const y = new Date(now)
      y.setDate(y.getDate() - 1)
      return { fromDate: formatDate(y), tillDate: formatDate(y) }
    }
    default:
      return { fromDate: '', tillDate: '' }
  }
}

export default function useFeedbackList() {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  // Filters
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [typeFilter, setTypeFilter] = useState('All')
  const [periodFilter, setPeriodFilterState] = useState('0')
  const [fromDate, setFromDate] = useState('')
  const [tillDate, setTillDate] = useState('')
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const handlePeriodChange = (val) => {
    setPeriodFilterState(val)
    const { fromDate: fDate, tillDate: tDate } = calculatePeriodDates(val)
    if (fDate || tDate) {
      setFromDate(fDate)
      setTillDate(tDate)
    }
  }

  useEffect(() => {
    setPage(1)
  }, [companyFilter, branchFilter, courseFilter, batchFilter, typeFilter, fromDate, tillDate, searchText])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchFeedbackList({
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        batch: batchFilter,
        type: typeFilter,
        fromDate,
        tillDate,
        searchText,
      })
      setRecords(res.records)
      setTotalCount(res.totalRecords)
    } catch (e) {
      console.error('Failed to load feedback list', e)
      appToast.error('Failed to load feedback records.')
    } finally {
      setLoading(false)
    }
  }, [companyFilter, branchFilter, courseFilter, batchFilter, typeFilter, fromDate, tillDate, searchText])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const handler = () => loadData()
    window.addEventListener('cloud-feedback-updated', handler)
    return () => window.removeEventListener('cloud-feedback-updated', handler)
  }, [loadData])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const pagedRecords = records.slice((page - 1) * pageSize, page * pageSize)
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(totalCount, page * pageSize)

  const goToNextPage = () => {
    setPage((prev) => Math.min(totalPages, prev + 1))
  }

  const goToPreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1))
  }

  const handleResetFilters = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setBatchFilter('All')
    setTypeFilter('All')
    setPeriodFilterState('0')
    setFromDate('')
    setTillDate('')
    setSearchText('')
  }

  const options = {
    companies: [
      { value: 'All', label: 'All Institutes' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    types: [
      { value: 'All', label: 'All Types' },
      { value: '1', label: 'Feedback' },
      { value: '2', label: 'Suggestion' },
      { value: '3', label: 'Problem' },
    ],
    periods: [
      { value: '0', label: 'Period (All)' },
      { value: '1', label: 'Current Year' },
      { value: '2', label: 'Current Month' },
      { value: '3', label: 'Current Week' },
      { value: '4', label: 'Last Year' },
      { value: '5', label: 'Last Month' },
      { value: '6', label: 'Last Week' },
      { value: '7', label: 'Next Year' },
      { value: '8', label: 'Next Month' },
      { value: '9', label: 'Next Week' },
      { value: '12', label: 'Today' },
      { value: '13', label: 'Yesterday' },
    ],
  }

  return {
    state: {
      records,
      pagedRecords,
      loading,
      totalCount,
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      typeFilter,
      periodFilter,
      fromDate,
      tillDate,
      searchText,
      options,
      page,
      pageSize,
      totalPages,
      rangeStart,
      rangeEnd,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setBatchFilter,
      setTypeFilter,
      setPeriodFilter: handlePeriodChange,
      setFromDate,
      setTillDate,
      setSearchText,
      setPage,
      setPageSize,
      goToNextPage,
      goToPreviousPage,
      handleResetFilters,
      loadData,
    },
  }
}
